import React, { useEffect, useState } from "react";
import { Card, PageHeader, Btn, Input } from "../components/UI";
import { FileUp, ShieldCheck, CheckCircle2, Loader2, UploadCloud, Trash2, Database } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

export default function FirstPartyData() {
  const [files, setFiles] = useState([]);
  const [name, setName] = useState("");
  const [csvText, setCsvText] = useState("");
  const [fileMeta, setFileMeta] = useState(null);
  const [consent, setConsent] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activating, setActivating] = useState(null);

  const load = () => api.get("/first-party/voter-files").then(r => setFiles(r.data));
  useEffect(() => { load(); }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setName(f.name.replace(/\.[^.]+$/, ""));
    const reader = new FileReader();
    reader.onload = () => {
      setCsvText(reader.result);
      const lines = String(reader.result).split("\n").filter(Boolean);
      setFileMeta({ rows: lines.length - 1, header: lines[0] });
    };
    reader.readAsText(f);
  };

  const upload = async () => {
    if (!consent) { toast.error("Confirm lawful collection first"); return; }
    if (!csvText) { toast.error("Choose a CSV file"); return; }
    setUploading(true);
    try {
      const r = await api.post("/first-party/voter-files", { name: name || "Voter file", csv_text: csvText, consent_confirmed: true });
      toast.success(`Hashed ${r.data.hash_count} identifiers (SHA-256) from ${r.data.row_count} rows`);
      setCsvText(""); setFileMeta(null); setName(""); setConsent(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const activate = async (vf) => {
    setActivating(vf.id);
    try {
      const r = await api.post(`/first-party/voter-files/${vf.id}/activate`);
      toast.success(`Audience live — CRM segment #${(r.data.stackadapt_segment_id || '').toString().slice(-6)}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Activation failed");
      load();
    } finally { setActivating(null); }
  };

  const del = async (id) => {
    await api.delete(`/first-party/voter-files/${id}`);
    load();
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        eyebrow="Voter File Onboarding"
        title="First-Party Data"
        subtitle="Upload voter and supporter lists — PII is SHA-256 hashed server-side, then activated as a programmatic CRM audience."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2">
          <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-3">Upload voter file (CSV)</div>
          <label className="block border-2 border-dashed border-white/10 rounded-md p-8 text-center cursor-pointer hover:border-cyan-400/40 transition-colors" data-testid="fp-upload">
            <FileUp className="w-8 h-8 mx-auto text-slate-500" />
            <div className="mt-2 text-sm text-slate-400">{fileMeta ? `${fileMeta.rows.toLocaleString()} data rows detected` : "Drop CSV or click to browse — needs an email or phone column"}</div>
            {fileMeta && <div className="mt-1 text-xs text-slate-600 font-mono truncate">{fileMeta.header}</div>}
            <input type="file" accept=".csv" className="hidden" onChange={onFile} />
          </label>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input label="List name" value={name} onChange={(e) => setName(e.target.value)} placeholder="GA supporters — June" data-testid="fp-name" />
          </div>

          <div className="mt-4 rounded-md border border-amber-400/30 bg-amber-400/5 p-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-amber-300 shrink-0" />
              <div>
                <div className="text-amber-300 text-sm font-semibold">Lawful collection consent</div>
                <p className="text-xs text-slate-300 mt-1">
                  I confirm this data was collected lawfully with required opt-in/opt-out controls and may be used for political advertising.
                </p>
                <label className="mt-2 inline-flex items-center gap-2 text-sm text-slate-200">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="fp-consent" />
                  I confirm
                </label>
              </div>
            </div>
          </div>

          <div className="mt-4 flex justify-end">
            <Btn onClick={upload} disabled={uploading || !csvText} data-testid="fp-hash-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />} Hash & store (SHA-256)
            </Btn>
          </div>
        </Card>

        <Card>
          <div className="text-xs uppercase tracking-wider text-slate-400 font-medium mb-3">Pipeline</div>
          <ul className="space-y-2 text-sm text-slate-300">
            {[
              "Parse CSV & detect email / phone columns",
              "Normalize identifiers",
              "SHA-256 hash server-side — raw PII never stored",
              "Store consent status",
              "Activate → CRM segment (LiveRamp)",
              "Audience appears in My Audiences for targeting",
            ].map(s => <li key={s} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-cyan-300 shrink-0 mt-0.5" />{s}</li>)}
          </ul>
          <div className="mt-4 text-[11px] text-amber-300/80 border border-amber-400/20 rounded-md p-2.5">
            Note: the ad platform validates hashed emails against LiveRamp's identity graph — at least 100 real, matchable email addresses are required. Test/synthetic emails will be rejected.
          </div>
        </Card>
      </div>

      <Card padded={false}>
        <div className="px-5 py-3 border-b border-white/10">
          <h3 className="font-display text-lg font-bold">Voter files</h3>
        </div>
        <table className="w-full text-sm" data-testid="voter-files-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
              <th className="px-5 py-3">Name</th>
              <th className="px-5 py-3">Rows</th>
              <th className="px-5 py-3">Hashed IDs</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {files.map(vf => (
              <tr key={vf.id} className="border-b border-white/5">
                <td className="px-5 py-2.5 text-white">{vf.name}</td>
                <td className="px-5 py-2.5 font-mono text-xs text-slate-400">{vf.row_count?.toLocaleString()}</td>
                <td className="px-5 py-2.5 font-mono text-xs text-slate-400">{vf.hash_count?.toLocaleString()} <span className="text-slate-600">({vf.valid_emails} email / {vf.valid_phones} phone)</span></td>
                <td className="px-5 py-2.5">
                  <span className={`pill ${vf.status === "activated" ? "pill-active" : vf.status === "activation_failed" ? "pill-rejected" : "pill-pending"}`}>
                    {vf.status}{vf.stackadapt_segment_id && ` · #${(vf.stackadapt_segment_id || '').toString().slice(-6)}`}
                  </span>
                  {vf.last_error && <div className="text-[10px] text-rose-300 mt-1 max-w-xs truncate">{vf.last_error}</div>}
                </td>
                <td className="px-5 py-2.5 text-right">
                  <div className="flex justify-end gap-2">
                    {vf.status !== "activated" && (
                      <Btn variant="secondary" className="!px-3 !py-1 text-xs" onClick={() => activate(vf)} disabled={activating === vf.id} data-testid={`fp-activate-${vf.id}`}>
                        {activating === vf.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Database className="w-3.5 h-3.5" />} Activate audience
                      </Btn>
                    )}
                    <button onClick={() => del(vf.id)} className="text-slate-500 hover:text-rose-300 p-1" data-testid={`fp-delete-${vf.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {files.length === 0 && <tr><td colSpan="5" className="px-5 py-8 text-center text-sm text-slate-500">No voter files yet — upload a CSV above.</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
